import React, { useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { range } from 'lodash';
import { useTranslation } from 'react-i18next';
import { SkeletonItem, Text } from '@fluentui/react-components';
import {
  Chat32Regular,
  ChevronLeft32Regular,
  Dismiss32Regular,
  Settings32Regular,
} from '@fluentui/react-icons';
import {
  ChatLockMode,
  ChatThreadType,
  GuardianChatThreadResponse,
} from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { FileItem } from '@mnspro-cloud/services';
import { ChatAvatar } from '../ChatAvatar/ChatAvatar';
import { ChatSettings } from '../ChatSettings/ChatSettings';
import { ChatInput } from '../ChatInput/ChatInput';
import { ChatIconsLocked } from '../ChatIcons/ChatIconsLocked';
import { Button } from '../../Button';
import { ChatMessageSkeleton } from '../ChatMessageSkeleton/ChatMessageSkeleton';
import { ChatMessageBubble } from '../ChatMessageBubble/ChatMessageBubble';
import {
  GuardianChatMessage,
  GuardianChatMessageLocalSender,
  MetadataAttachment,
} from '../GuardianChat.interface';
import { useChatContextOptional } from '../ChatContext';
import { ChatParticipantPickerDialog } from '../ChatParticipantPickerDialog/ChatParticipantPickerDialog';
import { clearChatDraft, useChatDraft } from '../drafts';
import './ChatDetails.scss';

export interface ChatDetailsProps {
  thread?: GuardianChatThreadResponse;
  messages?: GuardianChatMessage[] | null;
  selfAcsUserId?: string;
  onMessageSend?: (message: string, attachment?: MetadataAttachment | null) => void;
  onBackClick?: () => void;
}

export const ChatDetails: React.FC<ChatDetailsProps> = (props) => {
  const context = useChatContextOptional();
  const { t } = useTranslation();

  const thread = props.thread ?? context?.selectedThread;
  const messages = props.messages ?? context?.messages;
  const selfAcsUserId = props.selfAcsUserId ?? context?.selfAcsUserId ?? '';
  const onMessageSend = props.onMessageSend ?? context?.sendMessage;
  const onBackClick = props.onBackClick ?? context?.deselectThread;
  const addParticipantsDialogVisible = context?.addParticipantsDialogVisible ?? false;
  const addParticipantsDialogShowTeachers = context?.addParticipantsDialogShowTeachers;
  const setAddParticipantsDialogVisible = context?.setAddParticipantsDialogVisible;
  const addParticipantsToActiveThread = context?.addParticipantsToActiveThread;
  const loadMessagesNextPage = context?.loadMessagesNextPage;
  const hasMoreMessages = context?.hasMoreMessages;
  const messagesLoadingState = context?.messagesLoadingState ?? (messages ? 'loaded' : 'idle');
  const uploadedFile = context?.uploadedFile;
  const [draft, patchDraft] = useChatDraft(thread?.id);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [messageAttachment, setMessageAttachment] = useState<FileItem | null>(null);
  // The thread an in-flight attachment upload was started for, so a resolved
  // upload is written into the correct thread's draft and never bleeds into
  // another thread the user may have switched to meanwhile.
  const uploadTargetThreadRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevMessageCountRef = useRef<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const wasAtBottomRef = useRef(true);

  const isUserAtBottom = () => {
    const el = scrollContainerRef.current;
    if (!el) return true;

    const threshold = 50; // px
    return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  };

  useEffect(() => {
    prevMessageCountRef.current = null;

    return () => {
      // Closes settings panel, when changing threads
      setSettingsVisible(() => false);
    };
  }, [thread?.id]);

  // Keep the attachment chip in sync with the current thread's draft: restores
  // it when reopening a chat and reflects a newly persisted upload.
  useEffect(() => {
    setMessageAttachment(
      draft.attachment ? ({ name: draft.attachment.fileName } as FileItem) : null,
    );
  }, [thread?.id, draft.attachment]);

  // Write a resolved upload into the draft of the thread it was started for.
  // Guarded so switching threads while an upload is pending never copies it into
  // a different thread's draft.
  useEffect(() => {
    if (!uploadedFile || uploadTargetThreadRef.current !== thread?.id) {
      return;
    }
    if (uploadedFile.id !== draft.attachment?.id) {
      patchDraft({ attachment: uploadedFile });
    }
    uploadTargetThreadRef.current = null;
  }, [uploadedFile, thread?.id, draft.attachment?.id, patchDraft]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const handleScroll = () => {
      wasAtBottomRef.current = isUserAtBottom();
    };

    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!messages) {
      return;
    }

    const isInitialLoad = prevMessageCountRef.current === null;
    prevMessageCountRef.current = messages.length;

    if (isInitialLoad || wasAtBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: isInitialLoad ? 'instant' : 'smooth' });
    }
  }, [messages]);

  /**
   * @todo handle multiple wards
   */
  const getWardForMessageSender = (sender?: GuardianChatMessageLocalSender) => {
    const participant = thread?.participants.find(
      (p) => p.acsUserId !== undefined && p.acsUserId === sender?.acsUserId,
    );

    return participant?.wards.at(0);
  };

  const handleAttachmentClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        uploadTargetThreadRef.current = thread?.id ?? null;
        setMessageAttachment({ name: file.name } as FileItem);
        context?.uploadFileAttachment?.(file);
      }
      event.target.value = '';
    },
    [context, thread?.id],
  );

  const handleRemoveAttachment = useCallback(() => {
    uploadTargetThreadRef.current = null;
    setMessageAttachment(null);
    context?.uploadFileAttachment?.();
    patchDraft({ attachment: null });
  }, [context, patchDraft]);

  const handleSendMessage = (message: string) => {
    setMessageAttachment(null);
    onMessageSend?.(message, draft.attachment ?? null);
    if (thread?.id) {
      clearChatDraft(thread.id);
    }
  };

  return (
    <>
      {addParticipantsDialogVisible && (
        <ChatParticipantPickerDialog
          onCancel={() => setAddParticipantsDialogVisible?.(false)}
          onConfirm={(participants) => addParticipantsToActiveThread?.(participants)}
          open={addParticipantsDialogVisible}
          showTeachers={addParticipantsDialogShowTeachers}
        />
      )}
      <div
        className={cn('ChatDetails', {
          settingsVisible,
        })}
      >
        <div className="left flex flex-col flex-1">
          <div className="header">
            <Button
              className="backIcon"
              icon={<ChevronLeft32Regular />}
              appearance="transparent"
              onClick={onBackClick}
              aria-label={t('pc:chat:details:back_button_aria', {
                defaultValue: 'Zurück zur Chat-Übersicht',
              })}
            />
            {!thread && <SkeletonItem shape="circle" size={48} />}
            {thread && (
              <ChatAvatar
                variant={thread.threadType === ChatThreadType.direct ? 'direct' : 'group'}
                ward={thread.ward}
              />
            )}

            <div className="chatName">
              {!thread && <SkeletonItem size={28} />}
              {thread && (
                <>
                  {thread.lockConfiguration?.mode &&
                    [ChatLockMode.locked, ChatLockMode.timeBased].includes(
                      thread.lockConfiguration?.mode,
                    ) && <ChatIconsLocked />}
                  <Text weight="bold" size={500} className="chatNameLabel">
                    {thread.topic}
                  </Text>
                </>
              )}
            </div>
            <Button
              icon={<Settings32Regular className={cn('settingsIcon', { settingsVisible })} />}
              appearance="transparent"
              onClick={() => setSettingsVisible((prev) => !prev)}
              disabled={!thread}
              aria-label={t('pc:chat:details:toggle_settings_aria', {
                defaultValue: 'Einstellungen öffnen/schließen',
              })}
              aria-expanded={settingsVisible}
            />
          </div>
          <div
            ref={scrollContainerRef}
            className="chatDetailsContent flex-1"
            role="log"
            aria-live="polite"
          >
            {(!thread || !messages) && (
              <div className="contentInner">
                {range(0, 3).map((i) => (
                  <ChatMessageSkeleton key={i} />
                ))}
              </div>
            )}
            {thread && (
              <>
                {messagesLoadingState === 'loaded' && messages?.length === 0 && (
                  <div className="emptyState" role="status">
                    <div className="emptyStateIcon" aria-hidden="true">
                      <Chat32Regular />
                    </div>
                    <Text size={400} weight="semibold">
                      {t('pc:chat:no_messages_yet', {
                        defaultValue: 'Noch keine Nachrichten vorhanden',
                      })}
                    </Text>
                    <Text size={300}>
                      {t('pc:chat:no_messages_yet_description', {
                        defaultValue:
                          'Schreiben Sie die erste Nachricht, um die Unterhaltung zu beginnen.',
                      })}
                    </Text>
                  </div>
                )}
                {messagesLoadingState === 'loaded' && messages && messages.length > 0 && (
                  <div className="contentInner">
                    {loadMessagesNextPage && (
                      <div className="flex justify-center">
                        <Button
                          appearance="transparent"
                          onClick={loadMessagesNextPage}
                          disabled={!hasMoreMessages}
                        >
                          {t('pc:chat:details:load_older_messages', {
                            defaultValue: 'Ältere Nachrichten laden',
                          })}
                        </Button>
                      </div>
                    )}
                    {messages.map((m) => (
                      <ChatMessageBubble
                        key={`${m.metadata?._cid ?? '0'}-${m.id}`}
                        ward={getWardForMessageSender(m.sender)}
                        message={m}
                        isSelf={m.sender?.acsUserId === selfAcsUserId}
                        status={m.status}
                      />
                    ))}
                  </div>
                )}

                {messagesLoadingState === 'error' && <div>Messages Loading Error</div>}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>
          <div className="bottom">
            <div className="contentInner">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <ChatInput
                onSend={handleSendMessage}
                onRemoveReplyClick={() => {}}
                messageAttachment={messageAttachment}
                onAddAttachmentClick={handleAttachmentClick}
                onRemoveAttachmentClick={handleRemoveAttachment}
                hideImportant
                draftThreadId={thread?.id}
              />
            </div>
          </div>
        </div>
        {thread && settingsVisible && (
          <div className="right">
            <div className="header">
              <Button
                icon={<Dismiss32Regular />}
                appearance="transparent"
                onClick={() => setSettingsVisible((prev) => !prev)}
                disabled={!thread}
                aria-label={t('pc:chat:details:close_settings_aria', {
                  defaultValue: 'Einstellungen schließen',
                })}
              />
            </div>
            <div className="inner">
              <ChatSettings />
            </div>
          </div>
        )}
      </div>
    </>
  );
};
